import React, { useState, useRef, useEffect } from 'react';
import './Tooltip.scss';

/**
 * 툴팁 컴포넌트
 * 
 * @param {ReactNode} children - 툴팁을 트리거하는 요소
 * @param {string} content - 툴팁 내용
 * @param {string} position - 툴팁 위치 ('top', 'right', 'bottom', 'left', default: 'top')
 * @param {number} delay - 표시 지연 시간 (ms, default: 200)
 * @param {boolean} disabled - 비활성화 여부
 */
const Tooltip = ({
  children,
  content,
  position = 'top',
  delay = 200,
  disabled = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible && tooltipRef.current && triggerRef.current) {
      const tooltip = tooltipRef.current;
      const trigger = triggerRef.current;
      const rect = trigger.getBoundingClientRect();
      
      let top = 0;
      let left = 0;

      switch (position) {
        case 'top':
          top = rect.top - tooltip.offsetHeight - 8;
          left = rect.left + (rect.width / 2) - (tooltip.offsetWidth / 2);
          break;
        case 'bottom':
          top = rect.bottom + 8;
          left = rect.left + (rect.width / 2) - (tooltip.offsetWidth / 2);
          break;
        case 'left':
          top = rect.top + (rect.height / 2) - (tooltip.offsetHeight / 2);
          left = rect.left - tooltip.offsetWidth - 8;
          break;
        case 'right':
          top = rect.top + (rect.height / 2) - (tooltip.offsetHeight / 2);
          left = rect.right + 8;
          break;
        default:
          top = rect.top - tooltip.offsetHeight - 8;
          left = rect.left + (rect.width / 2) - (tooltip.offsetWidth / 2);
      }

      // 화면 경계 체크
      const padding = 8;
      if (top < padding) {
        top = padding;
      }
      if (left < padding) {
        left = padding;
      }
      if (top + tooltip.offsetHeight > window.innerHeight - padding) {
        top = window.innerHeight - tooltip.offsetHeight - padding;
      }
      if (left + tooltip.offsetWidth > window.innerWidth - padding) {
        left = window.innerWidth - tooltip.offsetWidth - padding;
      }

      setTooltipStyle({ 
        top: `${top}px`, 
        left: `${left}px`,
        position: 'fixed',
        zIndex: 10003
      });
    }
  }, [isVisible, position]);

  const handleMouseEnter = () => {
    if (disabled) return;
    
    timerRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setIsVisible(false);
  };

  const handleClick = () => {
    if (disabled) return;
    
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setIsVisible(!isVisible);
  };

  return (
    <div
      className="tooltip-wrapper"
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {children}
      {isVisible && content && (
        <div
          ref={tooltipRef}
          className={`tooltip tooltip-${position}`}
          style={tooltipStyle}
        >
          {content}
        </div>
      )}
    </div>
  );
};

export default Tooltip;

